// Derives a wire-format Zod schema from a Drizzle table.
//
// Goal: one source of truth (the Drizzle table) drives both server-side
// storage and the over-the-wire shape used by the RxDB replication
// adapter. The wire shape diverges from storage in two ways that this
// helper handles explicitly:
//   1. Date columns become ISO strings (`z.iso.datetime()`).
//   2. A soft-delete timestamp column is dropped in favour of RxDB's
//      `_deleted: boolean` tombstone convention.
//
// Anything that can't be inferred from the table — value bounds,
// minimum-length constraints, app-specific refinements — is passed in via
// `overrides`. RxDB-only metadata (primary key, indexes, maxLength
// hints, version) lives on the sibling `rxdbFromZod` helper.

import {
  type Column,
  getTableColumns,
  type InferSelectModel,
  type Table,
} from 'drizzle-orm';
import { z } from 'zod/v4';

export type WireFromDrizzleOptions<T extends Table> = {
  // Columns to drop entirely from the wire shape.
  omit?: ReadonlyArray<keyof T['_']['columns']>;
  // Override schemas for individual columns (e.g. `title: z => z.string().min(1)`).
  // Receives the `z` namespace so call sites don't need their own import.
  overrides?: Partial<Record<keyof T['_']['columns'], OverrideFn>>;
  // Name of the soft-delete timestamp column; replaced by `_deleted?: boolean`.
  softDelete?: keyof T['_']['columns'];
};

type OmittedKeys<T extends Table, Cfg extends WireFromDrizzleOptions<T>> =
  | (Cfg extends { omit: ReadonlyArray<infer O> }
      ? O extends string
        ? O
        : never
      : never)
  | (Cfg extends { softDelete: infer S }
      ? S extends string
        ? S
        : never
      : never);

type OverrideFn = (zod: typeof z) => z.ZodType;

type WireRow<
  T extends Table,
  Cfg extends WireFromDrizzleOptions<T>,
> = (Cfg extends { softDelete: string } ? { _deleted?: boolean } : object) & {
  [K in Exclude<keyof InferSelectModel<T>, OmittedKeys<T, Cfg>>]: WireValue<
    InferSelectModel<T>[K]
  >;
};

// Wrap each row field in a ZodType<value> so z.ZodObject's inference
// flows the right TS type out the other side of `.parse()` / `z.infer`.
type WireShape<T extends Table, Cfg extends WireFromDrizzleOptions<T>> = {
  [K in keyof WireRow<T, Cfg>]: z.ZodType<WireRow<T, Cfg>[K]>;
};

// Map a Drizzle column's TS type to its wire-side representation: Date
// instances ride as ISO strings (RxDB stores JSON only), everything else
// passes through unchanged.
type WireValue<V> = [V] extends [Date]
  ? string
  : [V] extends [Date | null]
    ? null | string
    : V;

const columnToZod = (column: Column): z.ZodType => {
  // Date columns always serialize as ISO strings on the wire — RxDB's
  // collection storage is JSON-only, so Date instances would be lost
  // anyway. Numbers get an `.int()` constraint when Drizzle marks the
  // column as integer-typed; anything we don't recognise falls through
  // to `z.unknown()` and the caller is expected to specify an override.
  switch (column.dataType) {
    case 'boolean':
      return z.boolean();
    case 'date':
      return z.iso.datetime();
    case 'json':
      return z.unknown();
    case 'number':
      return (column.columnType ?? '').toLowerCase().includes('int')
        ? z.number().int()
        : z.number();
    case 'string':
      return z.string();
    default:
      return z.unknown();
  }
};

export const wireFromDrizzle = <
  T extends Table,
  const Cfg extends WireFromDrizzleOptions<T> = WireFromDrizzleOptions<T>,
>(
  table: T,
  options: Cfg = {} as Cfg,
): z.ZodObject<WireShape<T, Cfg>> => {
  const columns = getTableColumns(table) as unknown as Record<string, Column>;

  const omitSet = new Set<string>((options.omit ?? []).map(String));
  if (options.softDelete) {
    omitSet.add(String(options.softDelete));
  }

  const overrides = (options.overrides ?? {}) as Record<string, OverrideFn>;

  const shape: Record<string, z.ZodType> = {};
  for (const [name, column] of Object.entries(columns)) {
    if (omitSet.has(name)) {
      continue;
    }

    const override = overrides[name];
    let fieldSchema = override ? override(z) : columnToZod(column);
    if (!column.notNull && !override) {
      fieldSchema = fieldSchema.nullable();
    }

    shape[name] = fieldSchema;
  }

  if (options.softDelete) {
    // RxDB's tombstone field is `_deleted` by convention; the leading
    // underscore is part of the wire contract, not stylistic.
    // eslint-disable-next-line canonical/id-match
    shape._deleted = z.boolean().optional();
  }

  return z.object(shape) as unknown as z.ZodObject<WireShape<T, Cfg>>;
};

export const toWireDocFromDrizzle = <
  T extends Table,
  const Cfg extends WireFromDrizzleOptions<T> = WireFromDrizzleOptions<T>,
>(
  table: T,
  row: InferSelectModel<T>,
  options: Cfg = {} as Cfg,
): WireRow<T, Cfg> => {
  const columns = getTableColumns(table) as unknown as Record<string, Column>;

  const omitSet = new Set<string>((options.omit ?? []).map(String));
  const softDeleteColumn = options.softDelete
    ? String(options.softDelete)
    : null;

  if (softDeleteColumn) {
    omitSet.add(softDeleteColumn);
  }

  const source = row as Record<string, unknown>;
  const doc: Record<string, unknown> = {};

  for (const name of Object.keys(columns)) {
    const value = source[name];

    if (name === softDeleteColumn) {
      if (value) {
        doc['_deleted'] = true;
      }

      continue;
    }

    if (omitSet.has(name)) {
      continue;
    }

    doc[name] = value instanceof Date ? value.toISOString() : value;
  }

  return doc as WireRow<T, Cfg>;
};
